import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { LineSegment, VictoryLabel, VictoryPie } from 'victory';
import styled from 'styled-components';
import styles from '../../../utility/styles';

const dummyPie = [
    { label: 'No Data Available', pct: 100 },
]

const Wrapper = styled.div`
    position: relative;
    width: 100%;
    height: 420px;
    @media only screen and ${styles.breakpoints.tablet} {
		& > .VictoryContainer {
			user-select: auto !important; 
        	touch-action: auto !important;
		}
	}
`

const LogoWrapper = styled.div`
    position: absolute;
    top: 50%;
    left: 50%;
    transform-origin: center;
    transform: translate(-50%, -50%);
    display: flex;
    flex-direction: column;
    align-items: center;
    @media screen and (max-width: 768px) {
        transform: scale(75%) translate(-66.6666666667%, -66.6666666667%);
    }
`

const colorScale = styles.colors.software_tracker_colors;

const smidge = 9;
const returnRadius = ({ datum, radius, innerRadius, data }) => {
    const pct = datum.pct;
    if (pct > 20) return radius - (radius - innerRadius) / 2;
    const smallPcts = data.filter(d => d.pct <= 20)
    const index = smallPcts.findIndex(d => d.version === datum.version);
    const initialSmidge = index === 0 ? smidge : 0;
    return radius + initialSmidge + (index * smidge);
}

const textAnchor = ({ datum }) => {
    const centerAngle = (datum.endAngle + datum.startAngle) / 2;
    if (datum.pct > 20) return 'middle'
    return centerAngle < 180 ? 'start' : 'end'
}

const TeslaLogo = ({ scale = 1 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="102" height="101" viewBox="0 0 102 101" fill="none" {...{ scale }}>
        <path fillRule="evenodd" clipRule="evenodd" d="M98.4319 16.3489C81.6819 9.04562 65.2891 6.70379 51 6.82287C36.7109 6.74348 20.3181 9.04562 3.56813 16.3886C1.58353 12.737 0.75 10.5539 0.75 10.5539C19.0877 3.29028 36.2346 0.829384 51 0.75C65.8051 0.829384 82.952 3.29028 101.25 10.5142C101.25 10.5142 100.655 12.3004 98.4319 16.3489ZM65.0905 21.7469L51.0395 100.853L36.9092 21.7469C23.6124 21.7469 19.3654 23.2155 18.77 29.209C18.77 29.209 9.7599 25.8749 5.23502 19.0479C22.9376 10.8316 40.7196 10.4744 40.7196 10.4744L51.0594 23.0723L61.3991 10.4744C61.3991 10.4744 79.1811 10.8316 96.8837 19.0479C92.3588 25.8749 83.3488 29.209 83.3488 29.209C82.7137 23.2155 78.5063 21.7469 65.0905 21.7469Z" fill="#2B3240" />
    </svg>
)

const RivianLogo = ({ scale = 1 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="134" height="134" viewBox="0 0 134 134" fill="none" {...{ scale }}>
        <path d="M119.97 62.98C114.12 56.95 82.478 24.3377 76.546 19.6268C74.4732 17.966 71.7848 16.75 68.4683 16.75C65.1518 16.75 62.4559 17.966 60.3906 19.6268C54.4611 24.3377 22.7911 56.95 16.9621 62.98C16.4596 63.5327 16.9169 64.392 17.6354 64.392L31.414 64.4021C32.8511 64.4021 33.2783 64.1508 34.1828 63.181L39.5846 57.3822C45.3056 51.214 59.2575 37.5962 64.3579 33.3049C66.2724 31.6918 68.4407 31.7471 68.4708 31.7471C68.501 31.7471 70.6693 31.6969 72.5838 33.3049C77.6842 37.5761 91.6361 51.219 97.357 57.3822L102.759 63.181C103.663 64.1508 104.091 64.4021 105.528 64.4021L119.306 64.392C120.025 64.397 120.447 63.5302 119.97 62.98ZM16.9621 70.7763C22.8112 76.8063 54.4536 109.449 60.3831 114.157C62.4534 115.815 65.1167 117.034 68.4608 117.034C71.8049 117.034 74.4732 115.818 76.5385 114.157C82.468 109.449 114.113 76.8088 119.962 70.7763C120.465 70.2235 120.007 69.3643 119.289 69.3643C116.367 69.3643 109.59 69.3542 105.513 69.3542C104.075 69.3542 103.648 69.6055 102.744 70.5753L97.342 76.3716C91.621 82.5398 77.6691 96.1576 72.5687 100.449C70.6542 102.062 68.4859 102.007 68.4558 102.007C68.4256 102.007 66.2573 102.057 64.3428 100.449C59.2424 96.1777 45.2905 82.5348 39.5696 76.3716L34.1677 70.5753C33.2632 69.6055 32.8361 69.3542 31.3989 69.3542L17.6204 69.3643C16.9118 69.3643 16.4847 70.2261 16.9621 70.7763ZM64.6418 77.6857L55.7274 68.8492C54.9737 67.9648 54.9737 66.8392 54.9737 66.8392C54.9737 66.8392 54.9234 65.7337 55.7274 64.8292C57.4359 62.8318 62.0087 58.3721 64.6418 55.9927C65.7096 54.9978 65.9659 54.3697 65.9659 53.1486V39.6138C65.9659 39.0685 65.3302 38.7293 64.8353 39.1866C59.0163 44.5734 46.0568 57.4173 41.7127 62.3921C39.6324 64.5955 39.8283 66.8392 39.8283 66.8392C39.8283 66.8392 39.6374 69.1004 41.7127 71.2863C46.0593 76.2611 59.049 89.105 64.8353 94.4918C65.3528 94.944 65.9659 94.6601 65.9659 94.0194V80.5097C65.9659 79.2535 65.7096 78.6505 64.6418 77.6605V77.6857ZM72.2924 77.6957L81.2067 68.8592C81.9605 67.9748 81.9605 66.8493 81.9605 66.8493C81.9605 66.8493 82.0107 65.7438 81.2067 64.8393C79.4982 62.8418 74.9255 58.3821 72.2924 56.0028C71.2245 55.0078 70.9683 54.3797 70.9683 53.1586V39.6891C70.9683 39.0484 71.5788 38.7645 72.0989 39.2168C77.9178 44.6036 90.8773 57.4475 95.2214 62.4222C97.2968 64.6257 97.1058 66.8694 97.1058 66.8694C97.1058 66.8694 97.2968 69.1306 95.2214 71.3165C90.8748 76.2912 77.8852 89.1351 72.0989 94.5219C71.5813 94.9742 70.9683 94.6903 70.9683 94.0496V80.5424C70.9733 79.2861 71.2245 78.6907 72.2924 77.6957Z" fill="#2B3240" />
    </svg>
)

const Logo = ({ type, scale }) => (
    type === 'tesla' ? <TeslaLogo {...{ scale }} /> : <RivianLogo {...{ scale }} />
)

/**
 * 
 * @param {{
 *   data: {version: string, pct: number}[],
 *   type: 'tesla' | 'rivian'
 * }} props
 * @returns 
 */
export default function PieChart({ data = [], type }) {
    const ref = useRef()
    const [dimensions, setDimensions] = useState({ width: 0, height: 0 })
    const chartData = React.useMemo(() => {
        if (!data.length) return dummyPie
        return [...data]
    }, [data])
    const labels = useMemo(() => data.map(d => d.version), [data]) // prevents the animation of the labels

    const onClick = useCallback(({ datum }) => {
        if (datum.label?.includes('No Data')) return
        if (datum.version?.includes('All')) return
        const version = datum.version.split('.').join('-')
        window.location.href = `/software-tracker/${type}/details/${version}`
    }, [data, type])

    useEffect(() => {
        const handleResize = () => {
            setDimensions({
                width: ref.current.offsetWidth > 420 ? 420 : ref.current.offsetWidth,
                height: ref.current.offsetHeight
            })
        }
        handleResize()
        window.addEventListener('resize', handleResize)
        return () => window.removeEventListener('resize', handleResize)
    }, [])
    return (
        <Wrapper {...{ ref }}>
            <VictoryPie
                {...dimensions}
                padding={55}
                colorScale={!data.length ? [styles.colors.darkGray] : colorScale}
                innerRadius={dimensions.width / 6}
                data={chartData}
                {...{ labels }}
                y='pct'
                padAngle={0.2}
                style={{
                    labels: {
                        fill: styles.colors.white,
                        fontSize: 10,
                        fontWeight: 600,
                        fontFamily: styles.fonts.nunito,
                    },
                    data: {
                        cursor: ({ datum }) => datum.label?.includes('No Data') || datum.version?.includes('All') ? 'default' : 'pointer'
                    }
                }}
                events={[
                    {
                        target: 'data',
                        eventHandlers: {
                            onClick: () => [{ target: 'data', mutation: onClick }]
                        }
                    }
                ]}
                labelRadius={returnRadius}
                labelComponent={<VictoryLabel {...{ textAnchor }} />}
                labelIndicator={<LineSegment style={{ stroke: styles.colors.white, strokeWidth: 2 }} />}
            />
            <LogoWrapper>
                <Logo type={type} scale={dimensions.width / 600} />
            </LogoWrapper>
        </Wrapper>
    )
}